import React, { useState, useEffect } from "react";
import axios from "axios";
import OrderList from "./OrderList";

const FactoryDashboard = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);

  // Filters per tab
  const [filtersByTab, setFiltersByTab] = useState({
    all: { search: "", startDate: "", endDate: "" },
    pending: { search: "", startDate: "", endDate: "" },
    processing: { search: "", startDate: "", endDate: "" },
    completed: { search: "", startDate: "", endDate: "" },
    cancelled: { search: "", startDate: "", endDate: "" },
  });

  const formatDate = (dateObj) => {
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setFiltersByTab((prev) => ({
      ...prev,
      completed: {
        ...prev.completed,
        startDate: formatDate(first),
        endDate: formatDate(last),
      },
      all: {
        ...prev.all,
        startDate: formatDate(first),
        endDate: formatDate(last),
      }
    }));

    setTimeout(() => {
      fetchOrders();
    }, 50);
  }, []);


  // Auto apply current month filter to 'all' and 'completed' tabs on tab switch
  // useEffect(() => {
  //   if (activeTab === "all" || activeTab === "completed") {
  //     const now = new Date();
  //     const first = new Date(now.getFullYear(), now.getMonth(), 1);
  //     const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  //     setFiltersByTab((prev) => ({
  //       ...prev,
  //       [activeTab]: {
  //         ...prev[activeTab],
  //         startDate: formatDate(first),
  //         endDate: formatDate(last),
  //       },
  //     }));
  //   }
  // }, [activeTab]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get("/api/orders/all");
      setOrders(res.data);

      const pendingCount = res.data.filter(
        (order) => order.status === "Pending" && !order.isSeen
      ).length;

      setNotificationCount(pendingCount);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching orders:", err);
    }
  };

  const withinRange = (orderDateStr, startStr, endStr) => {
    if (!orderDateStr) return false;
    const orderTime = new Date(orderDateStr).getTime();
    let startTime = -Infinity;
    let endTime = Infinity;

    if (startStr) startTime = new Date(startStr).getTime();
    if (endStr) endTime = new Date(endStr).getTime();

    return orderTime >= startTime && orderTime <= endTime;
  };

  const getFilteredOrdersForTab = (tabName) => {
    const filters = filtersByTab[tabName];

    return orders
      .slice()
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .filter((order) => {
        const matchesTab =
          tabName === "all" ||
          (order.status && order.status.toLowerCase() === tabName);

        const searchTerm = filters.search?.toLowerCase() || "";

        const matchesSearch =
          !searchTerm ||
          (order.orderId?.toLowerCase().includes(searchTerm)) ||
          (order.products?.some(p =>
            p.customName?.toLowerCase().includes(searchTerm) ||
            p.modelName?.toLowerCase().includes(searchTerm)
          ));

        let matchesDate = true;

        if (tabName === "completed") {
          const { startDate, endDate } = filters;

          if (startDate && endDate) {
            matchesDate =
              order.completionDate && withinRange(order.completionDate, startDate, endDate);
          } else {
            matchesDate = false; // no dates = don't show orders
          }
        } else {
          const orderDateField = order.orderDate;
          matchesDate =
            (!filters.startDate && !filters.endDate) ||
            (orderDateField && withinRange(orderDateField, filters.startDate, filters.endDate));
        }

        return matchesTab && matchesSearch && matchesDate;
      });
  };



  const filteredOrdersByTab = getFilteredOrdersForTab(activeTab);
  const currentFilters = filtersByTab[activeTab];

  const updateFilter = (field, value) => {
    setFiltersByTab((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], [field]: value },
    }));
  };

  const clearFilters = () => {
    setFiltersByTab((prev) => ({
      ...prev,
      [activeTab]: { search: "", startDate: "", endDate: "" },
    }));
  };

  return (
    <div className="px-2 sm:px-4 md:px-6">
      <h2 className="text-3xl font-bold mb-4">Factory Dashboard</h2>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 sm:gap-4 mb-6 overflow-x-auto">
        {["pending", "processing", "completed", "cancelled", "all"].map((tabName) => {
          const count = getFilteredOrdersForTab(tabName).length;

          return (
            <button
              key={tabName}
              className={`p-2 rounded ${activeTab === tabName ? "bg-blue-500 text-white" : "bg-gray-200"}`}
              onClick={() => setActiveTab(tabName)}
            >
              {tabName.charAt(0).toUpperCase() + tabName.slice(1)} ({count})
              {tabName === "pending" && notificationCount > 0 && (
                <span className="ml-2 bg-red-500 text-white px-2 rounded-full text-xs">
                  {notificationCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-4">
        <input
          type="text"
          placeholder="Search by Order ID or Custom Name"
          value={currentFilters.search}
          onChange={(e) => updateFilter("search", e.target.value)}
          className="border p-2 rounded w-full sm:w-1/3"
        />
        <div className="flex flex-col">
          <label className="text-sm font-semibold">Start Date</label>
          <input
            type="date"
            value={currentFilters.startDate}
            onChange={(e) => updateFilter("startDate", e.target.value)}
            className="border p-2 rounded"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold">End Date</label>
          <input
            type="date"
            value={currentFilters.endDate}
            onChange={(e) => updateFilter("endDate", e.target.value)}
            className="border p-2 rounded"
          />
        </div>
        {(currentFilters.search || currentFilters.startDate || currentFilters.endDate) && (
          <button
            onClick={clearFilters}
            className="bg-gray-500 text-white px-4 py-2 rounded self-end"
          >
            Clear Filter
          </button>
        )}
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <OrderList
          orders={filteredOrdersByTab}
          fetchOrders={fetchOrders}
          role="factory"
          tab={activeTab}
        />
      )}
    </div>
  );
};

export default FactoryDashboard;
